import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, apiErr } from "@/context/AuthContext";
import { Zap, Loader2 } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1631193816258-28b44b21e78b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjB3b3Jrc3BhY2V8ZW58MHx8fHwxNzg4OTExMDQ2fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name, companyName);
      toast.success("Bienvenue !");
      navigate("/");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Left visual */}
      <div className="relative hidden w-1/2 lg:block">
        <img src={BG} alt="Espace de travail" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-background/80" />
        <div className="absolute inset-0 grid-noise" />
        <div className="absolute bottom-0 left-0 p-12">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground">
              <Zap size={20} strokeWidth={2.5} />
            </div>
            <span className="font-display text-2xl font-bold">GestionPro</span>
          </div>
          <h2 className="font-display text-4xl font-bold leading-tight text-foreground">
            La gestion de votre<br />entreprise, unifiée.
          </h2>
          <p className="mt-4 max-w-md text-muted-foreground">
            Facturation, stocks, CRM et ressources humaines — pilotez tout depuis une
            seule plateforme claire et rapide.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center p-8 lg:w-1/2">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground">
                <Zap size={20} strokeWidth={2.5} />
              </div>
              <span className="font-display text-2xl font-bold">GestionPro</span>
            </div>
          </div>

          <h1 className="font-display text-3xl font-bold">
            {mode === "login" ? "Connexion" : "Créer votre entreprise"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {mode === "login"
              ? "Accédez à votre espace de gestion."
              : "1 mois d'essai gratuit, puis 100€/mois. Sans engagement."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Nom de l'entreprise
                  </label>
                  <input
                    data-testid="register-company"
                    required
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full border border-border bg-transparent px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary"
                    placeholder="Ex : ACME SARL"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Votre nom
                  </label>
                  <input
                    data-testid="register-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full border border-border bg-transparent px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary"
                    placeholder="Jean Dupont"
                  />
                </div>
              </>
            )}
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                E-mail
              </label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-border bg-transparent px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary"
                placeholder="vous@entreprise.com"
              />
            </div>
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Mot de passe
                </label>
                {mode === "login" && (
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-link">
                    Oublié ?
                  </Link>
                )}
              </div>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-border bg-transparent px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary"
                placeholder="••••••••"
              />
            </div>
            {mode === "register" && (
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Confirmer le mot de passe
                </label>
                <input
                  data-testid="register-confirm-password"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border border-border bg-transparent px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary"
                  placeholder="••••••••"
                />
                {confirmPassword && password !== confirmPassword && (
                  <p className="mt-1.5 text-xs text-red-500" data-testid="register-password-mismatch">
                    Les mots de passe ne correspondent pas
                  </p>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="flex w-full items-center justify-center gap-2 bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors duration-200 hover:bg-primary/90 disabled:opacity-60 active:scale-[0.98]"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {mode === "login" ? "Se connecter" : "Démarrer l'essai gratuit"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "login" ? "Pas encore de compte ?" : "Déjà inscrit ?"}{" "}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              data-testid="toggle-mode"
              className="text-primary hover:underline"
            >
              {mode === "login" ? "Créer mon entreprise" : "Se connecter"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
